import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { catchError, map, takeUntil } from 'rxjs/operators';

import { FinancialConfiguration } from '@gipi-financial/configuration/models/configuration.model';
import { RegistrationPaths } from '@gipi-paths/registration.paths';
import { SessionStorageService } from '@gipi-shared/services/session-storage.service';
import { ArrayUtil, GIPIAbstractCrudService, GIPIBaseService, GIPIPageModel, GIPISortModel, GIPIUuid, ObjectUtil, StringUtil } from '@gipisistemas/ng-core';
import { RegistrationTypesEmployee } from '../enums/types-employee.enum';
import { RegistrationEmployeeFilterDTO } from '../models/dto/employee-filter.dto';
import { RegistrationEmployee } from '../models/employee.model';

@Injectable({ providedIn: 'root' })
export class RegistrationEmployeeService extends GIPIAbstractCrudService<RegistrationEmployee, RegistrationEmployeeFilterDTO> {

    constructor(
        protected baseService: GIPIBaseService,
        private _sessionStorageService: SessionStorageService<any>,
    ) {
        super(RegistrationPaths.employee, 'v1', baseService);
    }

    getDescription(entity: RegistrationEmployee): string {
        let name: string = entity.person.name;

        const configuration: FinancialConfiguration = this._sessionStorageService.get('configuration');
        if (!ObjectUtil.isNull(configuration)) {
            name = configuration.showBusinessNameInReceiptQuery ? (entity.person.fantasyName ? entity.person.fantasyName : entity.person.name) : entity.person.name;
        }

        if (ObjectUtil.isNewModel(entity.person.legalPerson) || ObjectUtil.isNull(entity.person.legalPerson)) {
            const cpf: string = !StringUtil.isEmpty(entity.person.naturalPerson.cpf) ? StringUtil.format(entity.person.naturalPerson.cpf, '000.000.000-00') + ' - ' : '';
            return `${cpf}${name}`;
        } else {
            const cnpj: string = !StringUtil.isEmpty(entity.person.legalPerson.cnpj) ? StringUtil.format(entity.person.legalPerson.cnpj, '00.000.000/0000-00') + ' - ' : '';
            return `${cnpj}${name}`;
        }
    }

    findByValue<T = any>(value: string, page: number, size?: number, sort?: GIPISortModel, version?: string, types?: RegistrationTypesEmployee[], idsExcluded?: GIPIUuid[]): Observable<GIPIPageModel<T>> {
        let lValue: string = value;
        if (value) {
            lValue = StringUtil.removeAccents(value).trim();
        } else {
            lValue = '';
        }
        if ((!page) || (page && (page < 0))) {
            page = 0;
        }
        if ((!size) || (size && (size <= 0))) {
            size = 10;
        }
        let lSort: string = '';
        if (!ObjectUtil.isNull(sort) && !StringUtil.isEmpty(sort.field)) {
            lSort = `&sort=${sort.field},${sort.direction}`
        }
        let lType: string = '';
        if (!ArrayUtil.isEmpty(types)) {
            if (types.length === 1) {
                lType = `&type=${types[0]}`;
            } else if (types.length > 1) {
                lType = `&type=${types.join('&type=')}`;
            }
        }
        let lIdExcluded: string = '';
        if (!ArrayUtil.isEmpty(idsExcluded)) {
            if (idsExcluded.length === 1) {
                lIdExcluded = `&idExcluded=${idsExcluded[0]}`;
            } else if (idsExcluded.length > 1) {
                lIdExcluded = `&idExcluded=${idsExcluded.join('&idExcluded=')}`;
            }
        }

        return this.baseService.httpClient.get(this.url(`find-by-value?page=${page}&size=${size}&value=${lValue}${lSort}${lType}${lIdExcluded}`, version), this.options()).pipe(
            map(this.handleMapper),
            takeUntil(this.handleDestroy),
            catchError(this.handleError)
        );
    }

    findSeller(value: string, page: number, size: number = 10): Observable<GIPIPageModel<RegistrationEmployee>> {
        let lValue: string = value;
        if (value) {
            lValue = StringUtil.removeAccents(value).trim();
        } else {
            lValue = '';
        }
        if ((!page) || (page && (page < 0))) {
            page = 0;
        }
        if ((!size) || (size && (size <= 0))) {
            size = 10;
        }

        return this.baseService.httpClient.get(this.url(`find-seller?page=${page}&size=${size}&value=${lValue}`), this.options()).pipe(
            map(this.handleMapper),
            takeUntil(this.handleDestroy),
            catchError(this.handleError)
        );
    }

}
